import {
  Alert,
  Button,
  Checkbox,
  ComboboxItem,
  Divider,
  Group,
  Paper,
  Radio,
  Select,
  Stack,
  TextInput,
  Text,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IMaskInput } from "react-imask";
import { IconCircleCheck } from "@tabler/icons-react";
import { z } from "zod";
import { useUserStore } from "../../../store/user.store";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { IPatient } from "../../../models/patient";



const PatientSignUp = ({
  isGeneralClinic = true,
}: {
  isGeneralClinic?: boolean;
}) => {
  const events = useUserStore((state) => state.events);
  const addPatient = useUserStore((state) => state.addPatient);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  const generalScheme = {
    firstName: z.string().min(2, { message: "First name is too short" }),
    lastName: z.string().min(2, { message: "Last name is too short" }),
    phone: z.string().min(10, { message: "Phone number is too short" }),
    email: z.string().email({ message: "Invalid email address" }),
    eventId: z.string().min(1, { message: "Invalid appointment date" }),
    month: z.string().min(2, { message: "Invalid month" }),
    day: z.string().min(1, { message: "Invalid day" }),
    year: z.string().min(4, { message: "Invalid year" }),
  };

  const generalClinicScheme = {
    ...generalScheme,
    homeAddress: z.string().min(5, { message: "Home address is too short" }),
    city: z.string().min(2, { message: "City is too short" }),
    zipCode: z.string().min(5, { message: "Zip code is too short" }),
    officeStaffOnly: z.boolean(),
    isNewPatient: z.enum(["yes", "no"]),
  };

  const scheme = z.object(
    isGeneralClinic ? generalClinicScheme : generalScheme,
  );

  // list of years
  const currentYear = new Date().getFullYear();
  const years: ComboboxItem[] = [];
  for (let i = 0; i < 100; i++) {
    years.push({ value: `${currentYear - i}`, label: `${currentYear - i}` });
  }

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(scheme),
    initialValues: isGeneralClinic
      ? {
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
          eventId: events.length > 0 ? events[0].uid : "",
          month: "",
          day: "",
          year: "",
          homeAddress: "",
          city: "",
          zipCode: "",
          officeStaffOnly: false,
          isNewPatient: "no",
        }
        : {
            firstName: "",
            lastName: "",
            phone: "",
            email: "",
            eventId: events.length > 0 ? events[0].uid : "",
            month: "",
            day: "",
            year: "",
        },
  });

  const handleSetFieldValue = useCallback(() => {
    if (events.length === 0 || !events[0].uid) {
      return;
    }
    if (form.getValues().eventId === events[0].uid) {
      return;
    }
    form.setFieldValue("eventId", events[0].uid!);
    if (isGeneralClinic) {
      form.setFieldValue("officeStaffOnly", false);
      form.setFieldValue("isNewPatient", "no");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, isGeneralClinic]);

  useEffect(() => {
    handleSetFieldValue();
  }, [handleSetFieldValue]);

  if (completed) {
    return (
      <Paper withBorder radius="xl" p="xl" mt="md">
        <Stack align="flex-start">
          <Alert
            color="clinic"
            icon={<IconCircleCheck size={18} />}
            title="Thank you for signing up!"
            variant="light"
          >
            Your appointment has been reserved.
          </Alert>
          <Button variant="light" onClick={() => setCompleted(false)}>
            Sign up for another appointment
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper withBorder radius="xl" p={{ base: "md", sm: "xl" }} mt="md" shadow="xs">
      <form
        onSubmit={form.onSubmit(async (values) => {
          if (!values.eventId) {
            form.setFieldError("eventId", "Select an appointment date and time");
            return;
          }
          setLoading(true);
          const eventId = values.eventId;
          const payload: IPatient = {
            ...values,
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone,
            email: values.email,
            dob: dayjs(`${values.month} ${values.day} ${values.year}`)
              .toDate()
              .toISOString()
              .split("T")[0],
          };
          if (isGeneralClinic) {
            payload.address = values.homeAddress;
            payload.city = values.city;
            payload.zipCode = values.zipCode;
            payload.officeStaffOnly = values.officeStaffOnly;
            payload.isNewPatient = values.isNewPatient;
          }
          const addedPatient = await addPatient(eventId, payload);
          setLoading(false);
          if (addedPatient) {
            setCompleted(true);
            form.reset();
          }
        })}
      >
        <Stack gap="md">
          <div>
            <Title order={3}>Sign up for appointment</Title>
            <Text c="var(--my-dark)" size="sm" mt={4}>
              Choose an available appointment and complete the required patient information.
            </Text>
          </div>
          <Group grow>
            <TextInput
              {...form.getInputProps("firstName")}
              withAsterisk
              label="First Name"
              placeholder="Enter your first name"
            />
            <TextInput
              {...form.getInputProps("lastName")}
              withAsterisk
              label="Last Name"
              placeholder="Enter your last name"
            />
          </Group>

          <TextInput
            {...form.getInputProps("phone")}
            component={IMaskInput}
            // @ts-expect-error - IMaskInput props are not typed
            mask="(000) 000-0000"
            withAsterisk
            type="tel"
            label="Phone"
            placeholder="Enter your phone number"
          />
          <TextInput
            {...form.getInputProps("email")}
            withAsterisk
            label="Email"
            placeholder="Enter your email"
          />
          <Select
            value={form.getValues().eventId}
            label="Appointment Date & Time"
            withAsterisk
            data={events.map((event) => ({
              value: event.uid!,
              label: dayjs(event.date, "MMMM D, YYYY h:mm A").format(
                "MMMM D, YYYY h:mm A",
              ),
            }))}
            disabled={events.length === 0}
            nothingFoundMessage="No appointments available right now"
            {...form.getInputProps("eventId")}
          />
          <Divider label="Date of Birth" labelPosition="left" />
          <Group grow>
            <Select
              label="Select month"
              withAsterisk
              data={[
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ]}
              {...form.getInputProps("month")}
              placeholder="Select month"
            />
            <Select
              label="Select day"
              withAsterisk
              data={[
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "10",
                "11",
                "12",
                "13",
                "14",
                "15",
                "16",
                "17",
                "18",
                "19",
                "20",
                "21",
                "22",
                "23",
                "24",
                "25",
                "26",
                "27",
                "28",
                "29",
                "30",
                "31",
              ]}
              {...form.getInputProps("day")}
              placeholder="Select day"
            />
            <Select
              label="Select year"
              data={years}
              withAsterisk
              {...form.getInputProps("year")}
              placeholder="Select year"
            />
          </Group>
          {isGeneralClinic && (
            <>
              <Divider label="Home Address" labelPosition="left" />
              <TextInput
                {...form.getInputProps("homeAddress")}
                withAsterisk
                label="Address"
                placeholder="Enter your address"
              />
              <Group grow>
                <TextInput
                  {...form.getInputProps("city")}
                  withAsterisk
                  label="City"
                  placeholder="Enter your city"
                />
                <TextInput
                  {...form.getInputProps("zipCode")}
                  withAsterisk
                  label="Zip Code"
                  placeholder="Enter your zip code"
                />
              </Group>
            </>
          )}
          {isGeneralClinic && (
            <>
              <Divider label="Additional Information" labelPosition="left" />
              <Checkbox
                {...form.getInputProps("officeStaffOnly", { type: "checkbox" })}
                label="Please check this box if you need to ask a question to office staff and no doctor visit needed."
                key={form.key("officeStaffOnly")}
              />
              <Radio.Group
                label="Are you a new patient?"
                description="Please select if you are a new patient or not"
                withAsterisk
                name="isNewPatient"
                {...form.getInputProps("isNewPatient")}
              >
                <Stack gap="xs" mt="xs">
                  <Radio value="no" label="No, I am not a new patient" />
                  <Radio value="yes" label="Yes, I am a new patient" />
                </Stack>
              </Radio.Group>
            </>
          )}
          <Group justify="center" mt="sm">
            <Button loading={loading} disabled={loading} type="submit" radius="xl" size="md" color="clinic">
              Reserve a Spot for{" "}
              {isGeneralClinic ? "General Clinic" : "Behavioral Health"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
};

export default PatientSignUp;
